import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AgendaCalendar from "./AgendaCalendar";
import AgendaFilters from "./AgendaFilters";
import AgendaList from "./AgendaList";
import AppointmentDetailsDrawer from "./AppointmentDetailsDrawer";
import { parseAgendaDate, toDateKey } from "./agendaDateUtils";

const colorBySource = {
  appointment: "#0EA5E9",
  class_booking: "#10B981",
  class_session: "#F97316",
};

const colorByStatus = {
  cancelled: "#EF4444",
  no_show: "#DC2626",
  completed: "#22C55E",
  confirmed: "#14B8A6",
  booked: "#16A34A",
  scheduled: "#3B82F6",
};

export default function AgendaSummaryView({
  title,
  subtitle,
  loading,
  error,
  agenda,
  onRefresh,
}) {
  const allItems = Array.isArray(agenda?.items) ? agenda.items : [];
  const [selectedDate, setSelectedDate] = React.useState(toDateKey(new Date()));
  const [sourceFilter, setSourceFilter] = React.useState("all");
  const [selectedItem, setSelectedItem] = React.useState(null);

  React.useEffect(() => {
    if (selectedDate || !allItems.length) {
      return;
    }

    const firstAvailableDate = toDateKey(allItems[0]?.starts_at);

    if (firstAvailableDate) {
      setSelectedDate(firstAvailableDate);
    }
  }, [allItems, selectedDate]);

  React.useEffect(() => {
    if (!selectedItem?.id) {
      return;
    }

    const nextSelectedItem = allItems.find((item) => item.id === selectedItem.id);

    if (!nextSelectedItem) {
      setSelectedItem(null);
      return;
    }

    if (nextSelectedItem !== selectedItem) {
      setSelectedItem(nextSelectedItem);
    }
  }, [allItems, selectedItem]);

  const sourceOptions = React.useMemo(() => {
    const sources = Array.from(new Set(allItems.map((item) => item.source).filter(Boolean)));

    return [
      { value: "all", label: "All Sources" },
      ...sources.map((source) => ({
        value: source,
        label: source.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
      })),
    ];
  }, [allItems]);

  const resolveItemColor = React.useCallback((item) => {
    const directColor = item?.color;

    if (typeof directColor === "string" && /^#[0-9A-Fa-f]{6}$/.test(directColor)) {
      return directColor;
    }

    return colorByStatus[item?.status] || colorBySource[item?.source] || "#64748B";
  }, []);

  const visibleDayItems = React.useMemo(() => {
    return allItems
      .filter((item) => {
        if (toDateKey(item.starts_at) !== selectedDate) {
          return false;
        }

        if (sourceFilter !== "all" && item.source !== sourceFilter) {
          return false;
        }

        return true;
      })
      .sort((left, right) => {
        const leftDate = parseAgendaDate(left?.starts_at);
        const rightDate = parseAgendaDate(right?.starts_at);

        if (!leftDate && !rightDate) {
          return 0;
        }

        if (!leftDate) {
          return 1;
        }

        if (!rightDate) {
          return -1;
        }

        return leftDate.getTime() - rightDate.getTime();
      });
  }, [allItems, selectedDate, sourceFilter]);

  return (
    <Box sx={{ width: "100%", maxWidth: "1100px", margin: "0 auto" }}>
      <Card
        sx={{
          mb: 2,
          borderRadius: 4,
          background:
            "radial-gradient(circle at top right, rgba(14,165,233,0.22), transparent 40%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "common.white",
        }}
      >
        <CardContent>
          <Typography variant="overline" sx={{ opacity: 0.76 }}>
            Unified Schedule
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.78) }}>
            {subtitle}
          </Typography>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : null}

      {!loading && !error && agenda?.summary ? (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Card sx={{ flex: 1, borderRadius: 4 }}>
            <CardContent>
              <Typography variant="overline">Today</Typography>
              <Typography variant="h4">{agenda.summary.today_count ?? 0}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, borderRadius: 4 }}>
            <CardContent>
              <Typography variant="overline">Upcoming</Typography>
              <Typography variant="h4">{agenda.summary.upcoming_count ?? 0}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, borderRadius: 4 }}>
            <CardContent>
              <Typography variant="overline">Missed / Cancelled</Typography>
              <Typography variant="h4">{agenda.summary.missed_count ?? 0}</Typography>
            </CardContent>
          </Card>
        </Stack>
      ) : null}

      {!loading && !error ? (
        <Stack spacing={2}>
          <AgendaFilters
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            sourceFilter={sourceFilter}
            onSourceFilterChange={setSourceFilter}
            sourceOptions={sourceOptions}
          />

          {(agenda?.items || []).length === 0 ? (
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography color="text.secondary">No agenda items found for the selected period.</Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
                <Box sx={{ flex: 1.6 }}>
                  <AgendaCalendar
                    items={visibleDayItems}
                    resolveItemColor={resolveItemColor}
                    onSelectItem={setSelectedItem}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <AgendaList
                    items={visibleDayItems}
                    resolveItemColor={resolveItemColor}
                    onSelectItem={setSelectedItem}
                  />
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {sourceOptions
                  .filter((option) => option.value !== "all")
                  .map((option) => {
                    const sampleItem = allItems.find((item) => item.source === option.value);
                    const chipColor = resolveItemColor(sampleItem);

                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        variant="outlined"
                        sx={{
                          borderColor: alpha(chipColor, 0.4),
                          backgroundColor: alpha(chipColor, 0.1),
                          color: chipColor,
                        }}
                      />
                    );
                  })}
                <Chip label={`Selected Day: ${selectedDate || "N/A"}`} variant="outlined" />
              </Stack>
            </>
          )}
        </Stack>
      ) : null}

      <AppointmentDetailsDrawer
        open={Boolean(selectedItem)}
        item={selectedItem}
        color={selectedItem ? resolveItemColor(selectedItem) : "#64748B"}
        onSaved={onRefresh}
        onClose={() => setSelectedItem(null)}
      />
    </Box>
  );
}
